/**
 * One page of a list endpoint, exactly as the API's `PageResponse<T>` sends it.
 *
 * Any collection that grows with use is paged on the server. A screen shows a window of
 * it and asks for the next one; it never fetches the whole list and shows part of it,
 * which costs the full transfer to draw a fraction of it and leaves the count on screen
 * describing a list the page is already holding.
 */
export interface IPage<TItem> {
    items: TItem[];
    /** Zero-based. */
    page: number;
    size: number;
    /** Across the whole list, not this page -- what a "show more" counts down from. */
    totalItems: number;
    totalPages: number;
}

/** Whether there is another page after this one. The API sends no `hasNext` of its own. */
export const hasPageAfter = <TItem>(page: IPage<TItem>): boolean => page.page + 1 < page.totalPages;

/**
 * What a service method returns for a paged endpoint: everything `useInfiniteQuery` needs
 * and nothing it doesn't. Stated here, free of React Query's own types, so the service
 * layer and the hook wrapper agree on one shape rather than inferring two.
 */
export interface IPagedQueryOptions<TItem> {
    queryKey: readonly unknown[];
    queryFn: (context: { pageParam: number }) => Promise<IPage<TItem>>;
    initialPageParam: number;
    getNextPageParam: (lastPage: IPage<TItem>) => number | undefined;
}
